/**
 * Общий планировщик напоминаний.
 *
 * Раньше напоминания заводились на экранах: о дозах — на вкладке «Здоровье», о
 * привычках — на вкладке «Цели». Из этого следовало неочевидное и неверное
 * поведение: напоминание о таблетке появлялось, только если человек в тот день
 * заходил в таблетницу. Тот, кто открыл календарь и убрал телефон, не получал
 * ничего — а именно ему напоминание и нужнее всего.
 *
 * Поэтому планировщик живёт в каркасе приложения и не зависит от открытого
 * экрана. Он подписан на те же данные, что и экраны, поэтому расписание
 * перестраивается само: отметил привычку — напоминание снялось, перенёс дозу —
 * переехало.
 *
 * Возврат в приложение тоже пересобирает расписание. В браузере напоминание
 * держится на таймере вкладки, а фоновые вкладки браузер тормозит и выгружает;
 * пересборка при возвращении — единственный способ не потерять его молча.
 */

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { useDosesForDate } from '@/entities/dose/model/use-doses';
import { getEntriesInRange } from '@/entities/habit/model/habit-service';
import { useMedications } from '@/entities/medication/model/use-medications';
import { useProfile } from '@/entities/profile/model/use-profile';
import { cancelAll } from '@/shared/notifications/local-notifications';
import { syncHabitReminders } from '@/features/habit-schedule/model/habit-reminders';
import { syncDoseReminders } from '@/features/medication-schedule/model/dose-reminders';
import { selectCabinet } from '@/features/medicine-cabinet/model/cabinet';
import { syncExpiryReminders } from '@/features/medicine-cabinet/model/expiry-reminders';
import { readShowMedicationNames } from './notification-privacy';
import { db } from '@/shared/db/database';
import { getCurrentUserId } from '@/shared/db/session-context';
import { useToday } from '@/shared/lib/use-today';

/** Счётчик возвращений в приложение — им перезапускается пересборка. */
function useForegroundTick(): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => {
      if (document.visibilityState === 'visible') setTick((value) => value + 1);
    };

    document.addEventListener('visibilitychange', bump);
    return () => document.removeEventListener('visibilitychange', bump);
  }, []);

  return tick;
}

/**
 * Заводит напоминания о сегодняшних дозах и привычках.
 *
 * Вызывается один раз в каркасе приложения. Ничего не рисует и не запрашивает
 * разрешение: если уведомления не разрешены, все вызовы внутри тихо ничего не
 * делают, а приложение продолжает работать как журнал.
 */
export function useReminders(): void {
  // Без пересчёта в полночь напоминания продолжали бы идти о вчерашнем дне.
  const today = useToday();
  const foreground = useForegroundTick();

  const medications = useMedications();
  const doses = useDosesForDate(today);

  /*
    Выключатель напоминаний. Пока он выключен, расписание не заводится вовсе —
    и уже заведённое снимается: иначе напоминания, поставленные вчера,
    продолжали бы приходить сегодня, и выключатель выглядел бы сломанным.
  */
  const profile = useProfile();
  const remindersOn = profile?.notificationsEnabled ?? true;

  const habits = useLiveQuery(async () => {
    const userId = getCurrentUserId();
    if (!userId) return [];
    const rows = await db.habits.where('userId').equals(userId).toArray();
    return rows.filter((habit) => habit.deletedAt === null);
  }, []);

  const entries = useLiveQuery(() => getEntriesInRange(today, today), [today]);

  /*
    Показывать ли название лекарства в уведомлении. Живым запросом, а не
    разовым чтением: человек переключает это в настройках и ждёт, что
    следующее напоминание придёт уже по-новому.
  */
  const showNames = useLiveQuery(async () => {
    const userId = getCurrentUserId();
    return userId ? readShowMedicationNames(userId) : false;
  }, []);

  // `foreground` в зависимостях стоит намеренно: сам по себе он в расчёте не
  // участвует, но его рост означает возвращение в приложение — момент, когда
  // расписание нужно завести заново.
  useEffect(() => {
    if (!remindersOn) {
      void cancelAll();
      return;
    }

    if (!doses || !medications) return;
    syncDoseReminders(doses, medications, today, showNames ?? false);
  }, [doses, medications, today, foreground, showNames, remindersOn]);

  useEffect(() => {
    if (!remindersOn || !habits || !entries) return;
    syncHabitReminders(habits, entries, today);
  }, [habits, entries, today, foreground, remindersOn]);

  /*
    Сроки годности живут своей жизнью: их напоминания стоят на месяцы вперёд и
    не зависят ни от приёмов, ни от открытого экрана. Пересобираются они по тем
    же поводам — правка карточки, смена дня, возвращение в приложение.
  */
  useEffect(() => {
    if (!remindersOn || !medications) return;
    syncExpiryReminders(selectCabinet(medications), today, showNames ?? false);
  }, [medications, today, foreground, showNames, remindersOn]);
}
